import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { IdCard, Printer } from "lucide-react";

export default function Credenciales() {
  const { user } = useAuth();
  const [planteles, setPlanteles] = useState([]);
  const [plantelId, setPlantelId] = useState("");
  const [tipo, setTipo] = useState("ALUMNO"); // ALUMNO | DOCENTE
  const [alcance, setAlcance] = useState("TODOS"); // TODOS | GRUPO | INDIVIDUAL
  const [grupos, setGrupos] = useState([]);
  const [grupoId, setGrupoId] = useState("");
  const [personas, setPersonas] = useState([]);
  const [personaId, setPersonaId] = useState("");
  const [vigencia, setVigencia] = useState("");
  const [ciclo, setCiclo] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/planteles");
        setPlanteles(data);
        if (data.length) setPlantelId(data[0].id);
        // Default vigencia: 12 meses adelante
        const d = new Date(); d.setFullYear(d.getFullYear() + 1);
        setVigencia(d.toISOString().slice(0, 10));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    if (!plantelId) return;
    (async () => {
      try {
        const { data } = await api.get("/grupos", { params: { plantel_id: plantelId } });
        setGrupos(data);
        if (data.length && !ciclo) setCiclo(data[0].ciclo);
      } catch { /* ignore */ }
    })();
  }, [plantelId, ciclo]);

  useEffect(() => {
    if (!plantelId) return;
    (async () => {
      try {
        if (tipo === "DOCENTE") {
          const { data } = await api.get(`/planteles/${plantelId}/staff-list`);
          setPersonas((data.docentes || []).map((d) => ({
            id: d.id,
            nombre: [d.apellido_paterno, d.apellido_materno, d.nombre].filter(Boolean).join(" "),
            meta: d.email,
          })));
          setGrupoId("");
        } else if (alcance === "GRUPO" && grupoId) {
          const { data } = await api.get("/alumnos", { params: { grupo_id: grupoId } });
          setPersonas(data.map((a) => ({ id: a.id, nombre: [a.apellido_paterno, a.apellido_materno, a.nombre].filter(Boolean).join(" "), meta: a.curp })));
        } else if (alcance === "INDIVIDUAL" && tipo === "ALUMNO" && grupoId) {
          const { data } = await api.get("/alumnos", { params: { grupo_id: grupoId } });
          setPersonas(data.map((a) => ({ id: a.id, nombre: [a.apellido_paterno, a.apellido_materno, a.nombre].filter(Boolean).join(" "), meta: a.curp })));
        } else if (alcance === "TODOS" && tipo === "ALUMNO") {
          const gs = await api.get("/grupos", { params: { plantel_id: plantelId } });
          const all = [];
          for (const g of gs.data) {
            const { data } = await api.get("/alumnos", { params: { grupo_id: g.id } });
            data.forEach((a) => all.push({ id: a.id, nombre: [a.apellido_paterno, a.apellido_materno, a.nombre].filter(Boolean).join(" "), meta: `${g.grado}° "${g.letra}"` }));
          }
          setPersonas(all);
        } else {
          setPersonas([]);
        }
      } catch (e) { /* ignore */ }
    })();
  }, [plantelId, tipo, alcance, grupoId]);

  const buildUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("plantel_id", plantelId);
    params.set("tipo", tipo);
    params.set("alcance", alcance);
    if (grupoId) params.set("grupo_id", grupoId);
    if (personaId) params.set("persona_id", personaId);
    if (vigencia) params.set("vigencia", vigencia);
    if (ciclo) params.set("ciclo", ciclo);
    return `/credenciales/print?${params.toString()}`;
  }, [plantelId, tipo, alcance, grupoId, personaId, vigencia, ciclo]);

  const canGenerate = !!plantelId && (
    (alcance === "TODOS") ||
    (alcance === "GRUPO" && (tipo === "DOCENTE" || grupoId)) ||
    (alcance === "INDIVIDUAL" && personaId && (tipo === "DOCENTE" || grupoId))
  );

  return (
    <AppLayout>
      <div className="max-w-3xl">
        <div className="mb-4">
          <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Documentos imprimibles</div>
          <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Credenciales</h1>
          <p className="text-sm text-[#545454] mt-1">Genera credenciales tipo gafete (tamaño CR80) para alumnos o docentes. 6 por hoja carta; en modo individual sale frente y reverso en la misma hoja.</p>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded p-5 space-y-4">
          {["superadmin", "jefe_sector", "supervisor_zona"].includes(user?.role) && planteles.length > 1 && (
            <Field label="Plantel">
              <Select value={plantelId} onValueChange={setPlantelId}>
                <SelectTrigger data-testid="cred-plantel"><SelectValue /></SelectTrigger>
                <SelectContent>{planteles.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}

          <Field label="¿Para quién?">
            <div className="flex gap-2">
              {[["ALUMNO", "Alumnos"], ["DOCENTE", "Docentes"]].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`tipo-${v.toLowerCase()}`}
                  onClick={() => { setTipo(v); setAlcance("TODOS"); setGrupoId(""); setPersonaId(""); }}
                  className={`px-4 py-2 rounded border text-sm transition-colors ${tipo === v ? "bg-[#10312B] text-white border-[#10312B]" : "bg-white text-[#10312B] border-[#D1D5DB] hover:border-[#10312B]"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Alcance">
            <Select value={alcance} onValueChange={(v) => { setAlcance(v); setGrupoId(""); setPersonaId(""); }}>
              <SelectTrigger data-testid="cred-alcance"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos los {tipo === "ALUMNO" ? "alumnos" : "docentes"}</SelectItem>
                {tipo === "ALUMNO" && <SelectItem value="GRUPO">Por grupo</SelectItem>}
                <SelectItem value="INDIVIDUAL">Individual (una sola)</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          {alcance === "GRUPO" && tipo === "ALUMNO" && (
            <Field label="Grupo">
              <Select value={grupoId} onValueChange={setGrupoId}>
                <SelectTrigger data-testid="cred-grupo"><SelectValue placeholder="Selecciona grupo…" /></SelectTrigger>
                <SelectContent>
                  {grupos.map((g) => <SelectItem key={g.id} value={g.id}>{`${g.grado}° "${g.letra}" · Ciclo ${g.ciclo}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}

          {alcance === "INDIVIDUAL" && tipo === "ALUMNO" && (
            <Field label="Grupo del alumno">
              <Select value={grupoId} onValueChange={(v) => { setGrupoId(v); setPersonaId(""); }}>
                <SelectTrigger data-testid="cred-grupo-ind"><SelectValue placeholder="Selecciona grupo…" /></SelectTrigger>
                <SelectContent>
                  {grupos.map((g) => <SelectItem key={g.id} value={g.id}>{`${g.grado}° "${g.letra}" · Ciclo ${g.ciclo}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}

          {alcance === "INDIVIDUAL" && (tipo === "DOCENTE" || grupoId) && (
            <Field label={tipo === "ALUMNO" ? "Alumno" : "Docente"}>
              <Select value={personaId} onValueChange={setPersonaId}>
                <SelectTrigger data-testid="cred-persona"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent>
                  {personas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre} {p.meta ? `· ${p.meta}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Vigencia (fecha de expiración)">
              <Input type="date" value={vigencia} onChange={(e) => setVigencia(e.target.value)} data-testid="cred-vigencia" />
            </Field>
            <Field label="Ciclo escolar">
              <Input value={ciclo} onChange={(e) => setCiclo(e.target.value)} placeholder="2026-2027" data-testid="cred-ciclo" />
            </Field>
          </div>

          <div className="pt-2 flex items-center justify-between border-t border-[#F3F4F6]">
            <div className="text-xs text-[#6B7280] inline-flex items-center gap-1.5">
              <IdCard className="w-3.5 h-3.5" /> {personas.length ? `${personas.length} personas cargadas` : "Selecciona alcance"}
            </div>
            <Button
              data-testid="btn-generar-credenciales"
              disabled={!canGenerate}
              onClick={() => window.open(buildUrl, "_blank")}
              className="bg-[#235B4E] hover:bg-[#1D4A40] text-white"
            >
              <Printer className="w-4 h-4 mr-1.5" /> Generar credenciales
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1 block">{label}</label>
      {children}
    </div>
  );
}
